import { useEffect, useState } from "react";
import { api, formatBRL, API } from "@/lib/api";
import { Download } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { v: "em_analise", l: "Em análise" },
  { v: "aguardando_pagamento", l: "Aguardando pagamento" },
  { v: "pago", l: "Pago" },
  { v: "em_preparacao", l: "Em preparação" },
  { v: "enviado", l: "Enviado" },
  { v: "entregue", l: "Entregue" },
  { v: "cancelado", l: "Cancelado" },
];

export default function AdminOrders() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/admin/orders").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const update = async (orderId, updates) => {
    try {
      const { data } = await api.put(`/admin/orders/${orderId}`, updates);
      toast.success("Pedido atualizado");
      setSelected(data); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const download = async (o) => {
    const token = localStorage.getItem("duo_admin_token");
    const r = await fetch(`${API}/admin/orders/${o.id}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `recibo-${o.order_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="font-display text-3xl text-[#49392F] mb-8">Pedidos</h1>
      <div className="bg-white border border-[#C2A064]/20 overflow-x-auto">
        <table className="w-full text-[13px] min-w-[700px]">
          <thead className="text-left text-[#49392F]/70">
            <tr>
              <th className="p-3">Número</th><th className="p-3">Data</th><th className="p-3">Cliente</th>
              <th className="p-3">Status</th><th className="p-3 text-right">Total</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#49392F]/60">Sem pedidos.</td></tr>}
            {list.map((o) => (
              <tr key={o.id} className="border-t border-[#C2A064]/10 hover:bg-[#F8F5EF]">
                <td className="p-3">{o.order_number}</td>
                <td className="p-3">{new Date(o.created_at).toLocaleDateString("pt-BR")}</td>
                <td className="p-3">{o.customer?.name}<div className="text-[11px] text-[#49392F]/60">{o.customer?.phone}</div></td>
                <td className="p-3"><span className="text-[11px] tracking-[0.2em] uppercase bg-[#E8DCCB] px-2 py-1">{o.status_label}</span></td>
                <td className="p-3 text-right">{formatBRL(o.total)}</td>
                <td className="p-3 text-right"><button onClick={() => setSelected(o)} className="duo-btn duo-btn-secondary !py-2 !px-3 !text-[10px]" data-testid={`view-order-${o.order_number}`}>Ver</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <OrderDetail order={selected} onClose={() => setSelected(null)} onUpdate={update} onDownload={download} />}
    </div>
  );
}

function OrderDetail({ order, onClose, onUpdate, onDownload }) {
  const [status, setStatus] = useState(order.status);
  const [shippingCost, setShippingCost] = useState(order.shipping_cost ?? "");
  const [tracking, setTracking] = useState(order.tracking_code || "");
  const [adminNotes, setAdminNotes] = useState(order.admin_notes || "");

  return (
    <div className="fixed inset-0 bg-[#49392F]/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={onClose}>
      <div className="bg-white border border-[#C2A064]/30 w-full max-w-3xl mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#C2A064]/20">
          <div>
            <div className="font-display text-xl text-[#49392F]">Pedido {order.order_number}</div>
            <div className="text-[12px] text-[#49392F]/60">{new Date(order.created_at).toLocaleString("pt-BR")}</div>
          </div>
          <button onClick={() => onDownload(order)} className="duo-btn duo-btn-secondary"><Download size={14} /> PDF</button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/70 mb-2">Cliente</h4>
            <div className="text-[13px] text-[#49392F]">{order.customer?.name}</div>
            <div className="text-[12px] text-[#49392F]/70">{order.customer?.email}</div>
            <div className="text-[12px] text-[#49392F]/70">{order.customer?.phone}</div>

            <h4 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/70 mt-5 mb-2">Entrega</h4>
            <div className="text-[13px] text-[#49392F] leading-relaxed">
              {order.shipping_address?.street}, {order.shipping_address?.number} {order.shipping_address?.complement}<br />
              {order.shipping_address?.district} — {order.shipping_address?.city}/{order.shipping_address?.state}<br />
              CEP {order.shipping_address?.zip}
            </div>
            {order.customer_notes && <>
              <h4 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/70 mt-5 mb-2">Observação da cliente</h4>
              <div className="text-[13px] text-[#49392F]">{order.customer_notes}</div>
            </>}
          </div>

          <div>
            <h4 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/70 mb-2">Itens</h4>
            {order.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-[13px] py-1 border-b border-[#C2A064]/10 last:border-0">
                <span>{i.quantity}x {i.name} {i.size && `(${i.size})`}</span>
                <span>{formatBRL(i.subtotal)}</span>
              </div>
            ))}
            <div className="mt-3 text-right text-[14px] font-medium text-[#49392F]">Total: {formatBRL(order.total)}</div>
          </div>
        </div>
        <div className="p-6 border-t border-[#C2A064]/20 space-y-3">
          <h4 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F]/70">Gestão</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select className="duo-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="order-status-select">
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <input type="number" step="0.01" className="duo-input" placeholder="Valor do frete (R$)" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} data-testid="order-shipping-cost" />
            <input className="duo-input" placeholder="Código de rastreamento" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="order-tracking" />
          </div>
          <textarea rows={2} className="duo-input" placeholder="Anotações internas" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
          <div className="flex gap-3">
            <button onClick={() => onUpdate(order.id, { status, shipping_cost: shippingCost === "" ? null : Number(shippingCost), tracking_code: tracking || null, admin_notes: adminNotes })} className="duo-btn duo-btn-primary" data-testid="order-save">Salvar</button>
            <button onClick={onClose} className="duo-btn duo-btn-secondary">Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
